import { useIsMutating } from '@tanstack/react-query'
import { Link } from 'react-router'

import { Card, CardContent } from '@/components/ui/card'
import { formatDate } from '@/lib/formatters'
import {
  ENDORSEMENT_STEP_KEY,
  EndorsementStaffAction,
  endorsementSteps,
  type InternalEndorsement,
  useEndorsementStep,
} from '@/models/internalEndorsement'
import { PolicyChangeStatus } from '@/models/policyChange'
import { AskCustomerDialog } from './AskCustomerDialog'
import { confirmStep } from './confirmStep'
import { CorgiQuoteButtons, CorgiQuoteStatus } from './CorgiQuoteActions'
import { SOURCE_LABELS } from './endorsementCopy'
import { EndorsementStepBadge } from './EndorsementStepBadge'
import { MarkSentDialog } from './MarkSentDialog'
import { ReasonDialog } from './ReasonDialog'

/** Who asked and on which policies, the steps any time (ask the customer, decline, send it on), and the Corgi drafts. */
export function EndorsementHeader({ endorsement }: { endorsement: InternalEndorsement }) {
  const decline = useEndorsementStep(endorsementSteps.decline)
  const can = (action: EndorsementStaffAction) => endorsement.actions.includes(action)
  const stepping = useIsMutating({ mutationKey: ENDORSEMENT_STEP_KEY }) > 0
  const corgiQuotes = endorsement.quotes.filter((quote) => quote.corgiEligible)
  const sentOn = ![
    PolicyChangeStatus.DRAFT,
    PolicyChangeStatus.SUBMITTED,
    PolicyChangeStatus.NEEDS_INFO,
  ].includes(endorsement.status)

  return (
    <Card>
      <CardContent className="flex flex-col gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <h1 className="text-lg font-semibold">
            {endorsement.idShort} ·{' '}
            <Link
              to={`/insure/internal/customers/${endorsement.org.id}`}
              className="underline-offset-2 hover:underline"
            >
              {endorsement.org.name}
            </Link>
          </h1>
          <EndorsementStepBadge status={endorsement.status} step={endorsement.step} />
        </div>
        <div className="flex flex-col gap-0.5 text-sm text-neutral-600">
          {endorsement.policies.map((policy) => (
            <div key={policy.id}>
              {[policy.coverages.join(', '), policy.carrierName, policy.policyNumber]
                .filter(Boolean)
                .join(' · ')}
            </div>
          ))}
          {endorsement.submittedAt && (
            <div>
              Sent {formatDate(endorsement.submittedAt)} from {SOURCE_LABELS[endorsement.source]}
              {endorsement.signature && `, signed by ${endorsement.signature}`}
            </div>
          )}
        </div>
        <div className="flex flex-wrap gap-2 empty:hidden">
          {(can(EndorsementStaffAction.ASK) || can(EndorsementStaffAction.ASK_REPLY)) && (
            <AskCustomerDialog endorsement={endorsement} disabled={stepping} />
          )}
          {can(EndorsementStaffAction.DECLINE) && (
            <ReasonDialog
              trigger="Decline"
              disabled={stepping}
              title={`Decline ${endorsement.idShort}`}
              description="Every change is taken back off the roster, and the customer sees the reason."
              label="Why can't it go ahead?"
              confirmLabel="Decline request"
              onConfirm={(reason) =>
                confirmStep(decline.mutateAsync({ id: endorsement.id, reason }), 'Declined the request.')
              }
            />
          )}
          {can(EndorsementStaffAction.SEND_TO_CARRIERS) && (
            <MarkSentDialog endorsement={endorsement} disabled={stepping} />
          )}
          {sentOn &&
            corgiQuotes.map((quote) => (
              <CorgiQuoteButtons
                key={quote.id}
                endorsementId={endorsement.id}
                quote={quote}
                disabled={stepping}
                withCarrier={endorsement.status === PolicyChangeStatus.WITH_CARRIER}
              />
            ))}
        </div>
        {sentOn && corgiQuotes.map((quote) => <CorgiQuoteStatus key={quote.id} quote={quote} />)}
      </CardContent>
    </Card>
  )
}
